import React, { useContext, useEffect, useState } from 'react';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import * as yup from 'yup';
import ValidationDiv from '@utils/validation';
import useForm, { proxiedPropertiesOf } from '@hooks/useForm/useForm';
import { ApiContext } from '@api/api';
import { LovDTO, Pageable } from '@api/ringil-api.v2';
import { useTranslation } from 'react-i18next';
import useDispatchers from '@hooks/dispatchers/dispatchers.tsx';
import useDrivers from '@hooks/drivers/drivers.tsx';
import { Label } from '@components/styles.tsx';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import styled from 'styled-components';
import EditableDropdown, { DropdownItem } from '@components/RingilComponents/EditableDropdown.tsx';
import { PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';

interface ComponentProps {
  onComplete: () => void;
  data: ComponentData;
  expeditionId: string;
  showDriver: boolean;
  showDispatcher: boolean;
}

export interface ComponentData {
  carPlate: string;
  dispatcherId: string;
  driverId: string;
  driverName: string;
}

export const useCreateChangeVehicleInfo = () => {
  const [visible, setVisible] = useState(false);

  const ChangeVehicleInfo: React.FC<ComponentProps> = ({
                                                         expeditionId,
                                                         data,
                                                         onComplete,
                                                         showDispatcher,
                                                         showDriver,
                                                       }) => {
    const { t } = useTranslation('common');
    const dispatchers = useDispatchers(expeditionId);
    const drivers = useDrivers(expeditionId);
    const { expEditCarInfo, listCompanyUsersDrivers, companyUserDriverSetDeleted, createCompanyUserDriver, listCarPlates, deleteCarPlate } = useContext(ApiContext);

    const [selectedOptions, setSelectedOptions] = useState<DropdownItem[]>([]);
    const names = proxiedPropertiesOf<ComponentData>();

    const validationSchema = yup.object().shape({
      carPlate: yup.string().required(),
      dispatcherId: showDispatcher ? yup.string().required() : yup.string().nullable(),
      driverId: showDriver ? yup.string().required() : yup.string().nullable(),
    });

    const loadCarPlatesFromApi = (filter: string, pageable: Pageable, onSuccess: (pagedData: PagedData<DropdownItem>) => void) => {
      listCarPlates(
        (data) => {
          const mapped: PagedData<DropdownItem> = {
            data: data.map(plate => ({ id: plate.id, value: plate.number })),
            total: (data as any).total ?? data.length,
            page: (data as any).page ?? 0,
            size: (data as any).size ?? data.length,
          };
          onSuccess(mapped);
        },
      );
    };

    const loadDriversFromApi = (filter: string, pageable: Pageable, onSuccess: (pagedData: PagedData<DropdownItem>) => void) => {
      listCompanyUsersDrivers(
        pageable,
        filter,
        (data) => {
          const mapped: PagedData<DropdownItem> = {
            data: data.data.map(driver => ({ id: driver.id, value: driver.name })),
            total: (data as any).total ?? data.data.length,
            page: (data as any).page ?? 0,
            size: (data as any).size ?? data.data.length,
          };
          onSuccess(mapped);
        },
      );
    };

    const save = (d: ComponentData) => {
      expEditCarInfo(expeditionId, d, () => {
        setVisible(false);
        onComplete();
      });
    };

    const form = useForm<ComponentData>(
      validationSchema,
      data,
      d => {
        save(d);
      },
      false,
      false,
    );

    useEffect(() => {
      if (form.values.carPlate) {
        const plateNumbers = form.values.carPlate.split(' || ');
        const initialPlates = plateNumbers.map(plateNumber => ({
          id: plateNumber,
          value: plateNumber,
        }));
        setSelectedOptions(initialPlates);
      }
    }, [form.values.carPlate]);



    const handleSelectPlate = (plates: DropdownItem[]) => {
      setSelectedOptions(plates);
      form.setFieldValue(names.carPlate, plates.map(p => p.value).join(' || '));
    };

    // Handle plate deletion
    const handleDropDownDelete = (plate: DropdownItem, onDeleteSuccess: () => void) => {
      deleteCarPlate(plate.id, () => {
        setSelectedOptions(prevState => prevState.filter(p => p.id !== plate.id));
        onDeleteSuccess();
      });
    };

    const handleSelectDrivers = (drivers: DropdownItem[]) => {
      if (drivers.length > 0) {
        const driverToAdd = drivers[0];
        if (driverToAdd.isNew) {
          createCompanyUserDriver({ driverName: driverToAdd.value }, (createdDriver) => {
            form.setFieldValue(form.names.driverId, createdDriver.companyUserId);
            form.setFieldValue(form.names.driverName, driverToAdd.value);
          });
        } else {
          form.setFieldValue(form.names.driverId, driverToAdd.id);
          form.setFieldValue(form.names.driverName, driverToAdd.value);
        }
      }
    };

    const handleDeleteDriver = (driver: DropdownItem, onDeleteSuccess: () => void) => {
      if (driver.id) {
        companyUserDriverSetDeleted(driver.id, { isDeleted: true }, () => {
          if (form.values.driverId === driver.id) {
            form.setFieldValue(form.names.driverId, null);
            form.setFieldValue(form.names.driverName, null);
          }
          onDeleteSuccess();
        });
      }
    };

    return (
      <>
        {/*//
      @ts-ignore*/}
        <RingilSidebar
          visible={visible}
          onBack={() => setVisible(false)}
          onContinue={() => form.validateAndSend()}
        >
          <h3 className="p-mb-5" style={{ fontWeight: 'normal' }}>
            {t(`ChangeVehicleInfo.assignedCar`, `Assigned car`)}
          </h3>
          <div className="group-wrapper group-assign-vehicle">
            <div className="p-field">
              <div className="p-field">
                <EditableDropdown
                  loadOptionsFromApi={loadCarPlatesFromApi}
                  selectedOptions={selectedOptions}
                  onSelect={(plates: DropdownItem[]) => handleSelectPlate(plates)}
                  onDelete={(plate: DropdownItem, onDeleteSuccess) => handleDropDownDelete(plate, onDeleteSuccess)}
                  placeholder="Search car plates..."
                  label="SPZ"
                  multiSelect
                  mandatory
                  note={
                    <Note>
                      <b>{t(`InputPlate.note1`, `Máte auto s přívěsem?`)}</b>
                      <p>{t(`InputPlate.note2`, `Např. tahač SPZ 0001 | 1. přívěs SPZ 0002 | 2. přívěs SPZ 00003`)} </p>
                      <p>{t(`InputPlate.note3`, `Oddělte SPZ čárkami: SPZ 0001, SPZ 0002. SPZ 0003`)}</p>
                    </Note>
                  }
                />
                <ValidationDiv errors={form.validationErrors} path={'carPlate'} />
              </div>
            </div>
            {showDriver && (
              <div className="p-field">
                <EditableDropdown
                  loadOptionsFromApi={loadDriversFromApi}
                  selectedOptions={form.values.driverId ? [{
                    id: form.values.driverId,
                    value: form.values.driverName,
                  }] : []}
                  onSelect={(drivers: DropdownItem[]) => handleSelectDrivers(drivers)}
                  onDelete={(driver: DropdownItem, onDeleteSuccess) => handleDeleteDriver(driver, onDeleteSuccess)}
                  placeholder={t('driverDropdown.placeholder', 'Search drivers...')}
                  label={t('driverDropdown.label', 'Driver')}
                  mandatory
                />
                <ValidationDiv errors={form.validationErrors} path={names.driverId} />
              </div>
            )}

            {showDispatcher && (
              <div className="p-field">
                <Label title={t('ChangeVehicleInfo.dispatcher', 'Dispatcher')} />
                <Dropdown
                  value={form.values.dispatcherId}
                  options={dispatchers}
                  dataKey={'id'}
                  onChange={e => form.setFieldValue(names.dispatcherId, e.value)}
                  optionLabel="name"
                  optionValue="id"
                  placeholder={t('ChangeVehicleInfo.chooseDispatcher', 'Choose dispatcher')}
                />
                <ValidationDiv errors={form.validationErrors} path={names.dispatcherId} />
              </div>
            )}
          </div>
        </RingilSidebar>
      </>
    );
  };

  return { ChangeVehicleInfo, setVisible };
};

const Note = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 0.78rem;

  b {
    font-weight: 700;
    color: black;
    margin-top: 0.3rem;
  }

  p {
    margin: 0;
  }
`;
